import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import routes from "routes/routes";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/useDispatch";
import { setAccountLoginEmail } from "../../../redux/slice/account/accountSlice";
import { cleanEmail } from "util/validation";
import { withReturnTo } from "../common/returnTo";

/**
 * H-3a F2 — soft signup on the booking-confirmation surfaces: invites a guest
 * to create an account after booking. Renders nothing when accounts are
 * disabled for the tenant or the user is already logged in. Carries the
 * checkout email into the login flow (Redux flow state, never the URL) and
 * returns to the current page after login.
 */
const SoftSignupPrompt = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const accountsEnabled = useSelector(
    (state: RootState) => state.tenantConfig.config.accountsEnabled === true
  );
  const profile = useSelector((state: RootState) => state.account?.profile);
  const checkoutEmail = useSelector(
    (state: RootState) => state.bookingCreation?.personalData?.email
  );

  if (!accountsEnabled || profile) {
    return null;
  }

  const handleCreateAccount = () => {
    const email = checkoutEmail ? cleanEmail(checkoutEmail) : "";
    if (email) {
      dispatch(setAccountLoginEmail(email));
    }
    navigate(
      withReturnTo(
        routes.accountLoginPath,
        location.pathname + location.search
      )
    );
  };

  return (
    <div className="py-4 text-center">
      <p className="text-sm text-slate-600">
        {t("book_location.confirmation.create_account_description")}
      </p>
      <button
        type="button"
        onClick={handleCreateAccount}
        className="mt-1 inline-flex items-center justify-center rounded-md text-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {t("book_location.confirmation.create_account")}
      </button>
    </div>
  );
};

export default SoftSignupPrompt;
